// src/components/common/taskbox/StatsWidget.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Target, Plus, X, ArrowRight } from 'lucide-react';
import taskPerformanceService from '../../../services/taskPerformance.service';
import goalsService from '../../../services/goals.service';
import { useAuth } from '../../../hooks/useAuth';

const StatsWidget = ({ stats, period = 'weekly', theme }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [goals, setGoals] = useState([]);
    const [goalsStats, setGoalsStats] = useState({
        total: 0,
        active: 0,
        completed: 0,
        cancelled: 0,
        on_hold: 0,
        pending_review: 0
    });
    const [showGoalForm, setShowGoalForm] = useState(false);
    const [newGoal, setNewGoal] = useState({
        goal_type: 'completion_rate',
        target_value: '',
        description: ''
    });

    useEffect(() => {
        loadGoals();
    }, []);

    const loadGoals = async () => {
        try {
            // Load task performance goals
            const taskGoalsResponse = await taskPerformanceService.getGoals({ status: 'active' });
            setGoals(taskGoalsResponse.data || []);

            // Load goals statistics from Goals feature
            if (user?.id) {
                try {
                    const goalsFeatureResponse = await goalsService.getEmployeeGoals(user.id);
                    const allGoals = goalsFeatureResponse.data || [];

                    // Calculate statistics
                    const stats = {
                        total: allGoals.length,
                        active: allGoals.filter(g => g.status === 'active').length,
                        completed: allGoals.filter(g => g.status === 'completed').length,
                        cancelled: allGoals.filter(g => g.status === 'cancelled').length,
                        on_hold: allGoals.filter(g => g.status === 'on_hold').length,
                        pending_review: allGoals.filter(g => g.status === 'pending_review').length
                    };

                    setGoalsStats(stats);
                } catch (goalsError) {
                    console.warn('Error loading goals feature stats:', goalsError);
                }
            }
        } catch (error) {
            console.error('Error loading goals:', error);
        }
    };

    const handleCreateGoal = async (e) => {
        e.preventDefault();
        try {
            await taskPerformanceService.createGoal({
                ...newGoal,
                target_value: parseFloat(newGoal.target_value),
                period: period
            });
            setNewGoal({ goal_type: 'completion_rate', target_value: '', description: '' });
            setShowGoalForm(false);
            loadGoals();
        } catch (error) {
            console.error('Error creating goal:', error);
            alert('Failed to create goal');
        }
    };

    const handleGoToMyGoals = () => {
        // Navigate to Goals feature based on user role
        const userRole = user?.role || 'employee';
        switch (userRole) {
            case 'admin':
                navigate('/admin/goals');
                break;
            case 'manager':
                navigate('/manager/goals');
                break;
            case 'employee':
                navigate('/employee/goals');
                break;
            default:
                navigate('/employee/goals');
        }
    };

    const calculateGoalProgress = (goal) => {
        if (!stats) return 0;

        switch (goal.goal_type) {
            case 'completion_rate':
                return stats.completionRate || 0;
            case 'tasks_completed':
                return stats.completedTasks || 0;
            case 'on_time_rate':
                return stats.onTimeCompletionRate || 0;
            case 'velocity':
                return stats.taskVelocity || 0;
            default:
                return 0;
        }
    };

    const getGoalTypeLabel = (type) => {
        const labels = {
            completion_rate: 'Completion Rate',
            tasks_completed: 'Tasks Completed',
            on_time_rate: 'On-Time Rate',
            velocity: 'Task Velocity'
        };
        return labels[type] || type;
    };

    const getStatColorClass = (value, threshold = 70) => {
        if (value >= threshold) return 'text-green-600';
        if (value >= threshold * 0.7) return 'text-yellow-600';
        return 'text-red-600';
    };

    const getPriorityColor = (priority) => {
        const colors = {
            immediate: 'bg-red-600',
            urgent: 'bg-orange-600',
            next_in_line: 'bg-yellow-600',
            scheduled: 'bg-green-600',
            deferred: 'bg-gray-600'
        };
        return colors[priority] || 'bg-gray-600';
    };

    const formatPercentage = (value) => {
        return typeof value === 'number' ? `${Math.round(value)}%` : '0%';
    };

    if (!stats) {
        return (
            <div className={`rounded-xl p-6 border ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-lg font-semibold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        Performance Stats
                    </h3>
                </div>
                <div className="text-center py-8">
                    <TrendingUp className={`w-12 h-12 mx-auto mb-2 ${
                        theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
                    }`} />
                    <p className={`text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                        No performance data available yet.
                    </p>
                    <small className={`text-xs ${
                        theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                    }`}>
                        Complete some tasks to see your stats!
                    </small>
                </div>
            </div>
        );
    }

    return (
        <div className={`rounded-xl p-6 border ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
            <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                    Performance Stats
                </h3>
                <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                    theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                }`}>
                    {period}
                </span>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-2 gap-3 mb-6">
                <div className={`p-4 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                    <div className="flex items-center gap-3">
                        <div className="text-2xl">📊</div>
                        <div className="flex-1">
                            <div className={`text-xs font-medium ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Completion Rate
                            </div>
                            <div className={`text-xl font-bold ${getStatColorClass(stats.completionRate)}`}>
                                {formatPercentage(stats.completionRate)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={`p-4 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                    <div className="flex items-center gap-3">
                        <div className="text-2xl">✅</div>
                        <div className="flex-1">
                            <div className={`text-xs font-medium ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Tasks Done
                            </div>
                            <div className={`text-xl font-bold ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                {stats.completedTasks || 0}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={`p-4 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                    <div className="flex items-center gap-3">
                        <div className="text-2xl">⏰</div>
                        <div className="flex-1">
                            <div className={`text-xs font-medium ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                On-Time Rate
                            </div>
                            <div className={`text-xl font-bold ${getStatColorClass(stats.onTimeCompletionRate, 80)}`}>
                                {formatPercentage(stats.onTimeCompletionRate)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={`p-4 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                    <div className="flex items-center gap-3">
                        <div className="text-2xl">🚀</div>
                        <div className="flex-1">
                            <div className={`text-xs font-medium ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Velocity
                            </div>
                            <div className={`text-xl font-bold ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                {(stats.taskVelocity || 0).toFixed(1)}
                                <span className={`text-xs font-normal ml-1 ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                    tasks/day
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Task Breakdown */}
            <div className="mb-6">
                <h4 className={`text-sm font-semibold mb-3 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                    Task Overview
                </h4>
                <div className={`p-4 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-center justify-between">
                            <span className={`text-sm ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Total Tasks
                            </span>
                            <span className={`text-lg font-bold ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                {stats.totalTasks || 0}
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className={`text-sm ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Completed
                            </span>
                            <span className="text-lg font-bold text-green-600">
                                {stats.completedTasks || 0}
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className={`text-sm ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Pending
                            </span>
                            <span className="text-lg font-bold text-blue-600">
                                {stats.pendingTasks || 0}
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className={`text-sm ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Overdue
                            </span>
                            <span className="text-lg font-bold text-red-600">
                                {stats.overdueTasks || 0}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Priority Breakdown */}
            {stats.priorityBreakdown && Object.keys(stats.priorityBreakdown).length > 0 && (
                <div className="mb-6">
                    <h4 className={`text-sm font-semibold mb-3 ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        Priority Distribution
                    </h4>
                    <div className="space-y-3">
                        {Object.entries(stats.priorityBreakdown).map(([priority, count]) => {
                            const total = stats.totalTasks || 1;
                            const percentage = (count / total) * 100;

                            return (
                                <div key={priority} className={`p-3 rounded-lg border ${
                                    theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                                }`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className={`text-sm font-medium ${
                                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                                        }`}>
                                            {priority.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </span>
                                        <span className={`text-sm font-bold ${
                                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                                        }`}>
                                            {count}
                                        </span>
                                    </div>
                                    <div className={`h-2 rounded-full overflow-hidden ${
                                        theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
                                    }`}>
                                        <div
                                            className={`h-full ${getPriorityColor(priority)} transition-all duration-500`}
                                            style={{ width: `${percentage}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Goals Section */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h4 className={`text-sm font-semibold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        Goals & Targets
                    </h4>
                    <button
                        onClick={handleGoToMyGoals}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-green-600 hover:bg-green-700 text-white`}
                    >
                        <ArrowRight className="w-3 h-3" />
                        Go to My Goals
                    </button>
                </div>

                {/* Goals Statistics */}
                <div className={`grid grid-cols-3 gap-2 mb-4 p-3 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-700/30 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                    <div className="text-center">
                        <div className={`text-lg font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            {goalsStats.total}
                        </div>
                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            Total
                        </div>
                    </div>
                    <div className="text-center">
                        <div className={`text-lg font-bold text-green-600`}>
                            {goalsStats.active}
                        </div>
                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            Active
                        </div>
                    </div>
                    <div className="text-center">
                        <div className={`text-lg font-bold text-blue-600`}>
                            {goalsStats.completed}
                        </div>
                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            Completed
                        </div>
                    </div>
                    <div className="text-center">
                        <div className={`text-lg font-bold text-yellow-600`}>
                            {goalsStats.on_hold}
                        </div>
                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            On Hold
                        </div>
                    </div>
                    <div className="text-center">
                        <div className={`text-lg font-bold text-orange-600`}>
                            {goalsStats.pending_review}
                        </div>
                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            Pending Review
                        </div>
                    </div>
                    <div className="text-center">
                        <div className={`text-lg font-bold text-red-600`}>
                            {goalsStats.cancelled}
                        </div>
                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            Cancelled
                        </div>
                    </div>
                </div>

                <div className="space-y-3 max-h-64 overflow-y-auto">
                                      {goals.length === 0 ? null : (
                        goals.map(goal => {
                            const currentValue = calculateGoalProgress(goal);
                            const progress = Math.min((currentValue / goal.target_value) * 100, 100);
                            const isAchieved = currentValue >= goal.target_value;

                            return (
                                <div
                                    key={goal.id}
                                    className={`p-4 rounded-lg border ${
                                        isAchieved
                                            ? theme === 'dark'
                                                ? 'bg-green-900/20 border-green-700'
                                                : 'bg-green-50 border-green-200'
                                            : theme === 'dark'
                                                ? 'bg-gray-700/50 border-gray-600'
                                                : 'bg-gray-50 border-gray-200'
                                    }`}
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1">
                                            <div className={`text-sm font-medium flex items-center gap-2 ${
                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                            }`}>
                                                {getGoalTypeLabel(goal.goal_type)}
                                                {isAchieved && <span className="text-lg">🎯</span>}
                                            </div>
                                            {goal.description && (
                                                <div className={`text-xs mt-1 ${
                                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                }`}>
                                                    {goal.description}
                                                </div>
                                            )}
                                        </div>
                                        <div className={`text-sm font-bold ${
                                            isAchieved ? 'text-green-600' : theme === 'dark' ? 'text-white' : 'text-gray-900'
                                        }`}>
                                            {currentValue.toFixed(1)} / {goal.target_value}
                                            {goal.goal_type.includes('rate') ? '%' : ''}
                                        </div>
                                    </div>
                                    <div className={`h-2 rounded-full overflow-hidden ${
                                        theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
                                    }`}>
                                        <div
                                            className={`h-full transition-all duration-500 ${
                                                isAchieved ? 'bg-green-600' : 'bg-blue-600'
                                            }`}
                                            style={{ width: `${progress}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default StatsWidget;
